import AddIcon from '@mui/icons-material/Add'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Button, MenuItem, Select, TextField } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import React from 'react'
import { Link } from 'react-router-dom'
import { FilterSelect, filterSx } from '../../../components/filter-fields'
import GrantContent from '../../../components/GrantContent'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import * as Permisos from '../../../utils/Permisos'
import { COTIZACION_STATUS, formatCount, RamoMark, RAMOS } from '../common/quote-common'
import QuoteDrawer from '../drawer/quote-drawer'
import { NEW_QUOTE_LOCATION, PAGE_SIZES, TEMPLATES_LOCATION, useQuoteListModel } from './quote-list-model'
import type { QuoteTextFilter } from './quote-list-model'

const SKELETON_ROWS = [1, 2, 3, 4, 5, 6, 7, 8]

const columns = [
    { label: 'Cotización', className: 'min-w-0 md:min-w-[160px]' },
    { label: 'Contratante', className: 'hidden min-w-[200px] md:table-cell' },
    { label: 'Vencimiento', className: 'hidden whitespace-nowrap md:table-cell' },
    { label: 'Seguimiento', className: 'hidden whitespace-nowrap lg:table-cell' },
    { label: 'Estatus', className: 'hidden whitespace-nowrap md:table-cell' },
    { label: 'Acciones', className: 'hidden whitespace-nowrap text-right md:table-cell', sr: true },
]

const TEXT_FILTERS: Array<{ key: QuoteTextFilter; label: string; width: number }> = [
    { key: 'noCotizacion', label: 'Número de cotización', width: 190 },
    { key: 'nombre', label: 'Contratante', width: 240 },
]

const editBtnSx: SxProps<Theme> = {
    width: 'auto',
    minWidth: 'auto',
    height: 36,
    padding: '0 12px',
    borderRadius: '10px',
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '13px',
    lineHeight: 1,
    backgroundColor: 'transparent',
    color: 'rgb(var(--k-ink))',
    border: '1px solid rgb(var(--k-line-strong))',
    '&:hover': {
        backgroundColor: 'rgb(var(--k-accent-soft))',
        border: '1px solid rgb(var(--k-primary))',
        color: 'rgb(var(--k-accent-ink))',
        boxShadow: 'none',
    },
}

const stopRowClick = (event: React.MouseEvent) => event.stopPropagation()

const QuoteList = () => {
    const model = useQuoteListModel()
    const { filters, loading } = model

    const tableHead = (
        <thead>
            <tr className="text-left text-xs font-bold uppercase tracking-[0.05em] text-k-muted">
                {columns.map((column) => (
                    <th
                        key={column.label}
                        className={`sticky top-0 z-10 bg-k-surface px-2.5 py-3 font-bold ${column.className} ${column.label === 'Cotización' ? 'pl-4' : ''} ${column.sr ? 'pr-4 text-right' : ''}`}
                    >
                        {column.sr ? <span className="sr-only">{column.label}</span> : column.label}
                    </th>
                ))}
            </tr>
        </thead>
    )

    return (
        <KarteraAdminTemplate loading={false} padded={false} title="Cotizaciones">
            <QuoteDrawer
                cotizacionId={model.detailId}
                onClose={model.closeCotizacion}
                onEdit={model.editCotizacion}
                onDeleted={model.deleted}
                onChanged={model.changed}
            />

            <div className="flex min-h-0 flex-1 flex-col gap-[18px] bg-k-bg px-[clamp(16px,2.5vw,32px)] pb-10 pt-6">
                {loading && !model.initialLoadDone && (
                    <div className="flex min-h-0 flex-1 flex-col gap-[18px]">
                        <div>
                            <div className="mb-2 h-7 w-40 animate-pulse rounded bg-k-line md:h-8 md:w-48" />
                            <div className="hidden h-4 w-64 animate-pulse rounded bg-k-line md:block" />
                        </div>
                        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[18px] border border-k-line bg-k-surface">
                            <div className="shrink-0 border-b border-k-line px-4 py-3.5">
                                <div className="h-[42px] max-w-[380px] animate-pulse rounded-xl bg-k-line" />
                            </div>
                            <div className="min-h-0 flex-1 overflow-auto">
                                <table className="w-full table-fixed border-collapse md:min-w-[900px] md:table-auto">
                                    {tableHead}
                                    <tbody className="bg-k-surface">
                                        {SKELETON_ROWS.map((row) => (
                                            <tr key={row} className="border-t border-k-line">
                                                <td className="px-4 py-2.5">
                                                    <div className="flex items-center gap-3">
                                                        <div className="h-9 w-9 animate-pulse rounded-[11px] bg-k-line" />
                                                        <div>
                                                            <div className="h-3.5 w-20 animate-pulse rounded bg-k-line" />
                                                            <div className="mt-1 h-3 w-14 animate-pulse rounded bg-k-line" />
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="hidden px-2.5 py-2.5 md:table-cell"><div className="h-3.5 w-40 animate-pulse rounded bg-k-line" /></td>
                                                <td className="hidden px-2.5 py-2.5 md:table-cell"><div className="h-3.5 w-24 animate-pulse rounded bg-k-line" /></td>
                                                <td className="hidden px-2.5 py-2.5 lg:table-cell"><div className="h-3.5 w-28 animate-pulse rounded bg-k-line" /></td>
                                                <td className="hidden px-2.5 py-2.5 md:table-cell"><div className="h-5 w-20 animate-pulse rounded-full bg-k-line" /></td>
                                                <td className="hidden px-4 py-2.5 text-right md:table-cell"><div className="ml-auto h-9 w-28 animate-pulse rounded-[10px] bg-k-line" /></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {model.error && !loading && (
                    <div className="mb-6 rounded-lg border border-k-danger/30 bg-k-danger-soft p-4">
                        <div className="flex items-center">
                            <ErrorOutlineIcon className="mr-2 text-k-danger" style={{ fontSize: 20 }} />
                            <p className="text-sm text-k-danger-ink">{model.error}</p>
                        </div>
                        <Button
                            variant="contained"
                            onClick={model.reload}
                            className="mt-3 !bg-k-danger !text-white"
                        >
                            Reintentar
                        </Button>
                    </div>
                )}

                {model.initialLoadDone && !model.error && (
                    <div className="flex min-h-0 flex-1 flex-col gap-[18px]">
                        <header className="flex shrink-0 flex-wrap items-center justify-between gap-3.5 md:items-end">
                            <div className="flex flex-col gap-1">
                                <h1 className="m-0 text-xl font-extrabold tracking-[-0.03em] text-k-ink md:text-[28px]">Cotizaciones</h1>
                                <p className="m-0 hidden text-sm text-k-muted md:block">Propuestas enviadas a clientes y prospectos · {formatCount(model.totalItems)} registros</p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2.5">
                                <button
                                    type="button"
                                    aria-label="Actualizar"
                                    title="Actualizar"
                                    className="inline-flex h-11 w-11 items-center justify-center gap-2 rounded-xl border border-k-line bg-k-surface text-sm font-semibold text-k-ink hover:bg-k-hover md:w-auto md:px-4"
                                    onClick={model.reload}
                                >
                                    <RefreshIcon className={loading ? 'animate-spin' : ''} style={{ fontSize: 18 }} />
                                    <span className="sr-only md:not-sr-only">Actualizar</span>
                                </button>
                                <GrantContent permisos={[Permisos.COTIZACIONES_TEMP_R]}>
                                    <Link
                                        to={TEMPLATES_LOCATION.pathname}
                                        state={TEMPLATES_LOCATION.state}
                                        aria-label="Templates"
                                        title="Templates de cotización"
                                        className="inline-flex h-11 w-11 items-center justify-center gap-2 rounded-xl border border-k-line bg-k-surface text-sm font-semibold !text-k-ink no-underline hover:bg-k-hover md:w-auto md:px-4"
                                    >
                                        <DocumentScannerOutlinedIcon style={{ fontSize: 18 }} />
                                        <span className="sr-only md:not-sr-only">Templates</span>
                                    </Link>
                                </GrantContent>
                                <GrantContent permisos={[Permisos.COTIZACIONES_W]}>
                                    <Link
                                        to={NEW_QUOTE_LOCATION.pathname}
                                        state={NEW_QUOTE_LOCATION.state}
                                        aria-label="Nueva cotización"
                                        title="Nueva cotización"
                                        className="inline-flex h-11 w-11 items-center justify-center gap-2 rounded-xl bg-k-primary text-sm font-bold !text-white no-underline hover:brightness-105 hover:!text-white md:w-auto md:px-[18px]"
                                    >
                                        <AddIcon style={{ fontSize: 16 }} />
                                        <span className="sr-only md:not-sr-only">Nueva cotización</span>
                                    </Link>
                                </GrantContent>
                            </div>
                        </header>

                        <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[18px] border border-k-line bg-k-surface">
                            <div className="flex shrink-0 items-start gap-2.5 border-b border-k-line px-4 py-3.5">
                                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2.5">
                                    {TEXT_FILTERS.map((item) => (
                                        <TextField
                                            key={item.key}
                                            label={item.label}
                                            variant="outlined"
                                            size="small"
                                            value={filters[item.key]}
                                            sx={{ ...filterSx, width: { xs: '100%', sm: item.width } }}
                                            onChange={(event) => model.changeText(item.key, event.target.value)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter') model.submitText()
                                            }}
                                        />
                                    ))}
                                    <FilterSelect
                                        label="Estatus"
                                        minWidth={170}
                                        value={filters.status}
                                        onChange={(event) => model.changeStatus(event.target.value)}
                                    >
                                        <MenuItem value="">Todos</MenuItem>
                                        {COTIZACION_STATUS.map((item) => <MenuItem key={item.id} value={item.id}>{item.label}</MenuItem>)}
                                    </FilterSelect>
                                    <FilterSelect
                                        label="Ramo"
                                        minWidth={140}
                                        value={filters.ramo}
                                        onChange={(event) => model.changeRamo(event.target.value)}
                                    >
                                        <MenuItem value="">Todos</MenuItem>
                                        {RAMOS.map((ramo) => <MenuItem key={ramo} value={ramo}>{ramo}</MenuItem>)}
                                    </FilterSelect>
                                </div>
                                <button
                                    type="button"
                                    title="Limpiar filtros"
                                    aria-label="Limpiar filtros"
                                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center self-start rounded-xl bg-k-danger-soft text-k-danger-ink hover:brightness-95"
                                    onClick={model.clearFilters}
                                >
                                    <CloseIcon style={{ fontSize: 18 }} />
                                </button>
                            </div>

                            <div className={`min-h-0 flex-1 overflow-auto ${loading ? 'opacity-60' : ''}`}>
                                <table className="w-full table-fixed border-collapse text-sm md:min-w-[900px] md:table-auto">
                                    {tableHead}
                                    <tbody className="bg-k-surface">
                                        {model.rows.length === 0 && (
                                            <tr>
                                                <td className="px-4 py-12 text-center text-sm text-k-muted md:hidden">
                                                    No se encontraron cotizaciones con los filtros aplicados.
                                                </td>
                                                <td colSpan={columns.length} className="hidden px-4 py-12 text-center text-sm text-k-muted md:table-cell">
                                                    No se encontraron cotizaciones con los filtros aplicados.
                                                </td>
                                            </tr>
                                        )}
                                        {model.rows.map((row) => {
                                            const selected = model.detailId === row.id
                                            return (
                                                <tr
                                                    key={row.id}
                                                    className={`cursor-pointer border-t border-k-line transition-colors ${selected ? 'bg-k-accent-soft' : 'hover:bg-k-hover'}`}
                                                    onClick={() => model.openCotizacion(row.id)}
                                                >
                                                    <td className="max-w-[260px] px-4 py-2.5">
                                                        <div className="flex min-w-0 items-center gap-3 text-left">
                                                            <RamoMark ramo={row.ramo} boxed />
                                                            <span className="flex min-w-0 flex-col">
                                                                <span className="truncate font-bold tabular-nums text-k-ink">No. {row.noCotizacion ?? '—'}</span>
                                                                <span className="truncate text-xs text-k-muted md:hidden">{row.name || 'Sin contratante'}</span>
                                                                <span className="hidden truncate text-xs text-k-muted md:block">{row.ramo || 'Sin ramo'}</span>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="hidden max-w-[280px] px-2.5 py-2.5 md:table-cell">
                                                        <span className="flex min-w-0 flex-col">
                                                            <span className="truncate font-medium text-k-ink">{row.name || '—'}</span>
                                                            <span className="truncate text-xs text-k-muted">{row.tipo}</span>
                                                        </span>
                                                    </td>
                                                    <td className="hidden whitespace-nowrap px-2.5 py-2.5 md:table-cell">
                                                        <span className="flex flex-col">
                                                            <span className="text-[13px] tabular-nums text-k-ink-2">{row.vencimiento.label}</span>
                                                            {row.vencimiento.note && <span className={`text-[11px] font-bold ${row.vencimiento.noteClass}`}>{row.vencimiento.note}</span>}
                                                        </span>
                                                    </td>
                                                    <td className="hidden whitespace-nowrap px-2.5 py-2.5 lg:table-cell">
                                                        <span className="flex flex-col">
                                                            <span className={`text-[13px] font-semibold ${row.tracking.className}`}>{row.tracking.label}</span>
                                                            {row.tracking.detail && <span className="text-[11px] tabular-nums text-k-muted">{row.tracking.detail}</span>}
                                                        </span>
                                                    </td>
                                                    <td className="hidden whitespace-nowrap px-2.5 py-2.5 md:table-cell">
                                                        <span className={`inline-flex items-center gap-1.5 rounded-full px-[9px] py-[3px] text-xs font-bold ${row.status.className}`}>
                                                            <span className="h-1.5 w-1.5 rounded-full bg-current" />
                                                            {row.status.label}
                                                        </span>
                                                    </td>
                                                    <td className="hidden whitespace-nowrap px-4 py-2.5 text-right md:table-cell">
                                                        <div className="flex justify-end gap-2">
                                                            <Button
                                                                variant="outlined"
                                                                aria-label={`Ver cotización ${row.noCotizacion ?? ''}`}
                                                                sx={editBtnSx}
                                                                onMouseDown={stopRowClick}
                                                                onClick={(event) => {
                                                                    event.stopPropagation()
                                                                    model.openCotizacion(row.id)
                                                                }}
                                                            >
                                                                Ver
                                                            </Button>
                                                            <GrantContent permisos={[Permisos.COTIZACIONES_W]}>
                                                                <Button
                                                                    variant="outlined"
                                                                    aria-label={`Editar cotización ${row.noCotizacion ?? ''}`}
                                                                    sx={editBtnSx}
                                                                    onMouseDown={stopRowClick}
                                                                    onClick={(event) => {
                                                                        event.stopPropagation()
                                                                        model.editCotizacion(row.id)
                                                                    }}
                                                                >
                                                                    Editar
                                                                </Button>
                                                            </GrantContent>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-k-line bg-k-surface px-4 py-3 text-sm text-k-muted">
                                <span className="hidden md:inline">Mostrando {formatCount(model.rangeFrom)} a {formatCount(model.rangeTo)} de {formatCount(model.totalItems)} cotizaciones</span>
                                <div className="flex items-center gap-1">
                                    <button
                                        type="button"
                                        aria-label="Página anterior"
                                        disabled={model.page <= 1 || loading}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover disabled:opacity-40"
                                        onClick={() => model.setPage(model.page - 1)}
                                    >
                                        <ChevronLeftIcon fontSize="small" />
                                    </button>
                                    {model.pages.map((number) => (
                                        <button
                                            key={number}
                                            type="button"
                                            aria-current={number === model.page ? 'page' : undefined}
                                            disabled={loading}
                                            className={`inline-flex h-8 min-w-8 items-center justify-center rounded-md border px-2 text-sm font-medium disabled:opacity-40 ${number === model.page
                                                ? 'border-k-primary bg-k-accent-soft text-k-accent-ink'
                                                : 'border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover'}`}
                                            onClick={() => model.setPage(number)}
                                        >
                                            {number}
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        aria-label="Página siguiente"
                                        disabled={model.page >= model.totalPages || loading}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover disabled:opacity-40"
                                        onClick={() => model.setPage(model.page + 1)}
                                    >
                                        <ChevronRightIcon fontSize="small" />
                                    </button>
                                    <Select
                                        size="small"
                                        value={model.pageSize}
                                        disabled={loading}
                                        onChange={(event) => model.changePageSize(Number(event.target.value))}
                                        inputProps={{ 'aria-label': 'Registros por página' }}
                                        className="ml-1 h-8 min-w-[72px] rounded-md bg-k-surface text-sm text-k-ink [&_.MuiOutlinedInput-notchedOutline]:border-k-line [&_.MuiSelect-select]:min-h-0 [&_.MuiSelect-select]:py-0 [&_.MuiSelect-select]:leading-8 [&_.MuiSelect-select]:text-k-ink [&_.MuiSvgIcon-root]:text-k-muted"
                                        MenuProps={{
                                            slotProps: {
                                                paper: {
                                                    className: 'rounded-md border border-k-line bg-k-surface text-k-ink [&_.MuiMenuItem-root]:text-k-ink [&_.MuiMenuItem-root.Mui-selected]:bg-k-accent-soft [&_.MuiMenuItem-root.Mui-selected]:text-k-accent-ink',
                                                },
                                            },
                                        }}
                                    >
                                        {PAGE_SIZES.map((size) => (
                                            <MenuItem key={size} value={size}>{size}</MenuItem>
                                        ))}
                                    </Select>
                                </div>
                            </div>
                        </section>
                    </div>
                )}
            </div>
        </KarteraAdminTemplate>
    )
}

export default QuoteList
